import React, { useEffect, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import Dropdown from "../DropDown";
import { fetchWithCache } from "../../CacheDB/cached-Ref-Api";

interface ResourceStatusDropdownProps {
  selectedStatus: string;
  onStatusChange: (status: string) => void;
  disabled?: boolean;
  className?: string;
  onFocus?: () => void;
}

const ResourceStatusDropdown: React.FC<ResourceStatusDropdownProps> = ({
  selectedStatus,
  onStatusChange,
  disabled = false,
  className = "",
  onFocus,
}) => {
  const [statuses, setStatuses] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStatuses = async () => {
      const startTime = performance.now();
      try {
        const data = await fetchWithCache("resource-status", "/ref-data/resource-status");

        if (Array.isArray(data)) {
          setStatuses(data);
          datadogRum.addAction("Fetched Resource Statuses", {
            count: data.length,
          });
        } else {
          throw new Error("Invalid response format for resource statuses");
        }
      } catch (err: any) {
        setError("Failed to load resource status");
        datadogRum.addError("Error fetching resource statuses", { error: err });
        console.error("Error fetching resource statuses:", err);
      } finally {
        setLoading(false);
        const endTime = performance.now();
        datadogRum.addAction("Resource Status Fetch Duration", {
          duration: endTime - startTime,
        });
      }
    };

    fetchStatuses();
  }, []);

  const options = statuses.map((status) => ({
    value: status,
    label: status,
  }));

  return (
    <Dropdown
      options={options}
      selectedValue={selectedStatus}
      onValueChange={onStatusChange}
      disabled={disabled || loading || !!error}
      className={className}
      placeholder={loading ? "Loading..." : error ?? "Select Status"}
      onFocus={onFocus}
    />
  );
};

export default ResourceStatusDropdown;
